import React, { useCallback, useState } from 'react'
import { Box } from '@material-ui/core'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useDispatch } from 'react-redux'
import { isNil, noop } from 'lodash'

import { createSetupIntent, getCaptcha } from '../../api/api'
import PaymentMethod from '../common/PaymentMethod'
import ButtonSubmit from '../common/ButtonSubmit'
import { loadTeamPaymentMethodsStart } from '../../redux/modules/team'
import { enqueueSnackbar } from '../../redux/modules/snackbar'

export default function PaymentSelect({
  team,
  onSelectCard = async () => {},
  checkoutMode,
  btnCopy,
  submitting,
}) {
  const elements = useElements()
  const stripe = useStripe()
  const dispatch = useDispatch()

  const [creatingNewCard, setCreatingNewCard] = useState(false)

  const [primaryMethod, setPrimaryMethod] = useState(team?.plan?.primaryMethod)
  const updatePrimary = useCallback(id => setPrimaryMethod(id), [setPrimaryMethod])

  const createNewCard = async () => {
    setCreatingNewCard(true)
    const captchaToken = await getCaptcha()
    const setupIntentRes = await createSetupIntent({
      captchaToken,
      ownerType: 'ENTERPRISE',
      referenceId: team.id,
    })
    if (setupIntentRes.error) {
      dispatch(enqueueSnackbar(setupIntentRes.error))
      return
    }

    const reqBody = {
      payment_method: {
        card: elements.getElement(CardElement),
      },
    }

    const { setupIntent, error: confirmCardError } = await stripe.confirmCardSetup(
      setupIntentRes.clientSecret,
      reqBody,
    )
    if (confirmCardError) {
      dispatch(enqueueSnackbar(confirmCardError))
      setCreatingNewCard(false)
      return
    }
    setTimeout(() => dispatch(loadTeamPaymentMethodsStart(team.id)), 1200)

    await handleSelectCard(setupIntent.payment_method)
  }

  const handleSelectCard = async paymentIntentId => {
    if (!creatingNewCard) {
      setCreatingNewCard(true)
    }
    await onSelectCard(paymentIntentId)
    setCreatingNewCard(false)
  }

  const buttonBehavior = () => {
    if (checkoutMode) {
      if (primaryMethod) {
        return {
          copy: btnCopy || 'Confirm & Pay',
          action: () => handleSelectCard(primaryMethod),
        }
      } else {
        return {
          copy: btnCopy || 'Confirm & Pay',
          action: createNewCard,
        }
      }
    } else {
      if (!team?.plan?.primaryMethod && !primaryMethod) {
        return {
          copy: 'Add Card',
          action: createNewCard,
        }
      }
      if (primaryMethod === team?.plan?.primaryMethod) {
        return {
          copy: 'Close',
          action: () => {},
        }
      } else {
        return {
          copy: 'Set As Primary',
          action: () => {},
        }
      }
    }
  }

  const btnBehavior = buttonBehavior()
  const showSubmitButton =
    (!isNil(team?.plan) && primaryMethod !== team?.plan?.primaryMethod) || !primaryMethod
  return (
    <Box maxWidth="500px" textAlign="center" margin="auto" marginBottom={2}>
      <PaymentMethod
        isTeamContext
        teamId={team?.id}
        CardElement={CardElement}
        setPaymentMethodId={updatePrimary}
        paymentMethodId={primaryMethod}
        primaryMethodId={team?.plan?.primaryMethod}
        onError={noop}
        noPreselectIfEmpty={!checkoutMode}
      />
      {showSubmitButton && (
        <Box textAlign={checkoutMode ? 'center' : 'right'} marginY={2}>
          <ButtonSubmit
            submitting={creatingNewCard || submitting}
            variant="contained"
            onClick={btnBehavior.action}
            color="primary"
          >
            {btnBehavior.copy}
          </ButtonSubmit>
        </Box>
      )}
    </Box>
  )
}
